import { useMemo, useState } from "react";
import LedgerNumber from "@/components/LedgerNumber";
import { formatINR } from "@/lib/brand";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import CalculatorActions from "./CalculatorActions";

export default function TermCoverCalculator() {
  const [income, setIncome] = useState(1500000);
  const [liabilities, setLiabilities] = useState(5000000);
  const [education, setEducation] = useState(3000000);
  const [marriage, setMarriage] = useState(2000000);
  const [house, setHouse] = useState(0);
  const [existing, setExisting] = useState(0);

  const { eligibleCap, totalNeeds, netNeeded, recommended, gap } = useMemo(() => {
    const cap = Math.max(0, Number(income) || 0) * 20;
    const needs = (Number(liabilities) || 0) + (Number(education) || 0) + (Number(marriage) || 0) + (Number(house) || 0);
    const net = Math.max(0, needs - (Number(existing) || 0));
    const rec = Math.min(net, cap);
    const gp = Math.max(0, net - cap);
    return { eligibleCap: cap, totalNeeds: needs, netNeeded: net, recommended: rec, gap: gp };
  }, [income, liabilities, education, marriage, house, existing]);

  const netPct = eligibleCap + netNeeded === 0 ? 0 : Math.min(100, (netNeeded / Math.max(eligibleCap, netNeeded)) * 100);
  const capPct = eligibleCap + netNeeded === 0 ? 0 : Math.min(100, (eligibleCap / Math.max(eligibleCap, netNeeded)) * 100);
  const isSafe = netNeeded <= eligibleCap && netNeeded > 0;

  return (
    <div>
      <div className="grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5 space-y-5">
        <NumField label="Annual Income (₹)" value={income} onChange={setIncome} testId="term-income" />
        <NumField label="Outstanding Liabilities (₹)" value={liabilities} onChange={setLiabilities} testId="term-liabilities" />
        <NumField label="Children's Education Goal — Future Value (₹)" value={education} onChange={setEducation} testId="term-edu" />
        <NumField label="Children's Marriage Goal — Future Value (₹)" value={marriage} onChange={setMarriage} testId="term-marriage" />
        <NumField label="House / Other Major Goal (₹)" value={house} onChange={setHouse} testId="term-house" />
        <NumField label="Existing Life Cover (₹)" value={existing} onChange={setExisting} testId="term-existing" />
      </div>

      <div className="lg:col-span-7 card-paper p-6 md:p-8 ledger-rule" data-testid="term-result">
        <div className="eyebrow mb-2">Recommended Term Cover</div>
        <div className="text-[38px] md:text-[48px] leading-none text-ink">
          <LedgerNumber value={recommended} testId="term-recommended" />
        </div>

        <div className="mt-8">
          <div className="eyebrow mb-3">Net Needed vs. Eligible Cap</div>
          <div className="space-y-3">
            <BarRow label="Net Needed" value={formatINR(netNeeded)} pct={netPct} tone="brick" />
            <BarRow label="Eligible Cap (20× Income)" value={formatINR(eligibleCap)} pct={capPct} tone="green" />
          </div>
        </div>

        <div className="mt-8 divide-y hairline border-t hairline border-b">
          <Row label="Eligible Cap" value={formatINR(eligibleCap)} />
          <Row label="Liabilities + Goals" value={formatINR(totalNeeds)} />
          <Row label="Less: Existing Cover" value={`− ${formatINR(existing)}`} />
          <Row label="Net Cover Required" value={formatINR(netNeeded)} accent />
        </div>

        {netNeeded > 0 && (
          <div
            className={`mt-6 p-4 border-l-2 ${isSafe ? "border-[#16A34A] bg-[#16A34A]/5" : "border-[#DC2626] bg-[#DC2626]/5"}`}
            data-testid="term-message"
          >
            <div className={`flex items-start gap-2 ${isSafe ? "text-[#16A34A]" : "text-[#DC2626]"}`}>
              {isSafe ? <CheckCircle2 size={18} className="mt-0.5" /> : <AlertTriangle size={18} className="mt-0.5" />}
              <div>
                <div className="eyebrow" style={{ color: "inherit" }}>{isSafe ? "You're covered" : "There's a gap"}</div>
                <p className="text-[14px] leading-relaxed text-ink mt-1">
                  {isSafe
                    ? `The 20× income cap comfortably covers your net needs of ${formatINR(netNeeded)}. This recommendation is realistic and issuable.`
                    : `Your net cover requirement of ${formatINR(netNeeded)} exceeds the eligible 20× cap by ${formatINR(gap)}. Let's have a conversation about how to close this gap.`}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      <CalculatorActions
        testIdPrefix="term"
        buildPayload={() => ({
          type: "term",
          title: "Right Term Cover Calculator",
          one_liner: `a term cover assessment — net cover needed is ${formatINR(netNeeded)}, recommended issuable cover is ${formatINR(recommended)}`,
          inputs: [
            { label: "Annual Income", value: formatINR(income) },
            { label: "Outstanding Liabilities", value: formatINR(liabilities) },
            { label: "Education Goal (FV)", value: formatINR(education) },
            { label: "Marriage Goal (FV)", value: formatINR(marriage) },
            { label: "House / Other Goal", value: formatINR(house) },
            { label: "Existing Life Cover", value: formatINR(existing) },
          ],
          hero: { label: "Recommended Term Cover", value: formatINR(recommended) },
          summary: [
            { label: "Eligible Cap (20× Income)", value: formatINR(eligibleCap) },
            { label: "Liabilities + Goals", value: formatINR(totalNeeds) },
            { label: "Less: Existing Cover", value: `− ${formatINR(existing)}` },
            { label: "Net Cover Required", value: formatINR(netNeeded), accent: true },
            { label: "Gap over Cap", value: formatINR(gap) },
          ],
        })}
      />
    </div>
  );
}

function NumField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <input
        type="number" value={value} min={0}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        className="fld num" data-testid={testId}
      />
    </label>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between py-3">
      <span className="eyebrow">{label}</span>
      <span className={`num text-[16px] ${accent ? "text-brass-deep" : "text-ink"}`}>{value}</span>
    </div>
  );
}

function BarRow({ label, value, pct, tone }) {
  const color = tone === "brick" ? "bg-[#DC2626]" : tone === "green" ? "bg-[#16A34A]" : "bg-brass";
  return (
    <div>
      <div className="flex justify-between text-[12px] mb-1">
        <span className="num text-ink-soft">{label}</span>
        <span className="num text-ink">{value}</span>
      </div>
      <div className="w-full h-2 rounded-sm bg-paper border hairline overflow-hidden">
        <div style={{ width: `${pct}%` }} className={`h-full ${color} transition-all duration-500`} />
      </div>
    </div>
  );
}
